import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation, useParams } from 'react-router-dom';
import {
    CButton, CCard, CCardBody, CCardHeader, CAlert
} from '@coreui/react';

import { apiClient } from 'services/Api';
import { jwtDecode } from 'jwt-decode';
import DataTableComponent from "components/DataTableComponent";
import RecordDetailOffcanvas from "components/RecordDetailOffcanvas";
import AlertNotification, { triggerAlert } from 'components/globals/AlertNotification';

const EventEvaluationList = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [evaluations, setEvaluations] = useState([]);
    const location = useLocation();
    const [eventDesignation, setEventDesignation] = useState("");
    const [eventEffectiveness, setEventEffectiveness] = useState("");
    const [previewEvaluation, setPreviewEvaluation] = useState(null);

    const token = localStorage.getItem("access_token");
    const decodedToken = jwtDecode(token);
    const is_director = decodedToken.is_director;
    const user_id = decodedToken.user_id;

    useEffect(() => {
        const fetchEvaluations = async (eventId) => {
            try {
                const response = await apiClient.get(`/event-evaluations/?eventId=${eventId}`);
                setEvaluations(response.data.evaluations);
                setEventDesignation(response.data.eventDesignation);
                setEventEffectiveness(response.data.eventEffectiveness || "");
            } catch (error) {
                console.error('Error fetching evaluations:', error);
            }
        };

        fetchEvaluations(id);
    }, [location.pathname]);

    const handleDelete = async (evaluationId) => {
        try {
            await apiClient.delete(`/event-evaluations/${evaluationId}/`);
            setEvaluations(evaluations.filter((evaluation) => evaluation.id !== evaluationId));
            setPreviewEvaluation((current) => (current && current.id === evaluationId ? null : current));
        } catch (error) {
            triggerAlert('Erro ao eliminar a avaliação', 'success');
        }
    };

    const handleSetNotEvaluated = async () => {
        try {
            await apiClient.post(`/events/${id}/set-not-evaluated/`, {});
            triggerAlert('Formação/Evento definida como não avaliada com sucesso!', 'success');
            navigate('/formacoes-eventos');
        } catch (error) {
            console.error('Error setting event as not evaluated:', error);
        }
    };

    const getManagementRatingLabel = (rating) => {
        if (eventEffectiveness === "Não avaliada") return "Não avaliada";
        if (rating === null) return "";
        return rating < 2.5 ? "Não Eficaz" : "Eficaz";
    };

    const canEvaluate = eventEffectiveness !== "Não avaliada";

    const columns = [
        { name: "Nome", selector: (row) => row.participant_name, sortable: true, className: 'text-center' },
        { name: "Pontuação", selector: (row) => row.event_type === "Académico" ? row.academic_grade : row.final_rating, sortable: true, className: 'text-center' },
        { name: "Eficácia", selector: (row) => row.effectiveness, sortable: true },
        { name: "Data Auto-Avaliação", selector: (row) => row.date, sortable: true },
        ...(is_director ? [
            { name: "Pontuação Chefias", selector: (row) => row.management_final_rating, sortable: true },
            { name: "Eficácia Chefias", selector: (row) => getManagementRatingLabel(row.management_final_rating), sortable: true },
            { name: "Data Avaliação Chefias", selector: (row) => row.management_date, sortable: true },
        ] : []),
    ];

    return (
        <>
            <AlertNotification />
            <CCard>
                <CCardHeader className="d-flex justify-content-between align-items-center">
                    <h3 className="mb-0">{eventDesignation}</h3>
                    {is_director && !eventEffectiveness && (
                        <CButton color="secondary" onClick={handleSetNotEvaluated}>
                            Não Avaliar
                        </CButton>
                    )}
                </CCardHeader>
                <CCardBody>
                    <DataTableComponent
                        columns={columns}
                        data={evaluations}
                        onRowClicked={setPreviewEvaluation}
                    />
                </CCardBody>
            </CCard>
            <RecordDetailOffcanvas
                record={previewEvaluation}
                onHide={() => setPreviewEvaluation(null)}
                title="Avaliação"
                subtitle={previewEvaluation?.participant_name}
                fields={previewEvaluation ? [
                    { label: "Nome", value: previewEvaluation.participant_name },
                    {
                        label: "Pontuação",
                        value: previewEvaluation.event_type === "Académico"
                            ? previewEvaluation.academic_grade
                            : previewEvaluation.final_rating,
                    },
                    { label: "Eficácia", value: previewEvaluation.effectiveness },
                    { label: "Data Auto-Avaliação", value: previewEvaluation.date },
                    ...(is_director ? [
                        { label: "Pontuação Chefias", value: previewEvaluation.management_final_rating },
                        { label: "Eficácia Chefias", value: getManagementRatingLabel(previewEvaluation.management_final_rating) },
                        { label: "Data Avaliação Chefias", value: previewEvaluation.management_date },
                    ] : []),
                ] : []}
                headerActions={previewEvaluation && <>
                    {canEvaluate && (is_director || !previewEvaluation.effectiveness || (previewEvaluation.effectiveness && !previewEvaluation.management_final_rating)) && (
                        <CButton color="info" variant="outline" size="sm" onClick={() => navigate(`/avaliacao-formacoes-eventos/${previewEvaluation.id}`)}>
                            Avaliação
                        </CButton>
                    )}
                    {!previewEvaluation.effectiveness && previewEvaluation.participant === user_id && (
                        <CButton color="danger" size="sm" className="text-white" onClick={() => {
                            if (window.confirm("Tem a certeza que quer eliminar este registo?")) handleDelete(previewEvaluation.id);
                        }}>
                            Não Esteve Presente
                        </CButton>
                    )}
                </>}
            />
        </>
    );
};

export default EventEvaluationList;
